import { useEffect, useState } from 'react';
import { ML_CUTTING_DEFAULTS } from '../type/MLTypes';
import type { MLCuttingParams, MLProcessSummary } from '../type/MLTypes';

function isDeepEqualToDefaults(params: MLCuttingParams): boolean {
  return (
    params.fz === ML_CUTTING_DEFAULTS.fz &&
    params.ap === ML_CUTTING_DEFAULTS.ap &&
    params.ae === ML_CUTTING_DEFAULTS.ae &&
    params.vc === ML_CUTTING_DEFAULTS.vc &&
    params.nbDents === ML_CUTTING_DEFAULTS.nbDents &&
    params.diameterCut === ML_CUTTING_DEFAULTS.diameterCut
  );
}

interface UseMLCuttingParamsReturn {
  params: MLCuttingParams;
  setParam: (key: keyof MLCuttingParams, value: number) => void;
  resetToDefaults: () => void;
  isDirtyFromDefaults: boolean;
  isFromProcess: boolean;
  isUserEdited: boolean;
}

export function useMLCuttingParams(
  selectedProcess: MLProcessSummary | null
): UseMLCuttingParamsReturn {
  const [params, setParams] = useState<MLCuttingParams>({ ...ML_CUTTING_DEFAULTS });
  const [userEdited, setUserEdited] = useState(false);
  const [isFromProcess, setIsFromProcess] = useState(false);

  useEffect(() => {
    const next: MLCuttingParams = { ...ML_CUTTING_DEFAULTS };
    let fromProcess = false;

    if (selectedProcess !== null) {
      if (selectedProcess.ae !== null) {
        next.ae = selectedProcess.ae;
        fromProcess = true;
      }
      if (selectedProcess.ap !== null) {
        next.ap = selectedProcess.ap;
        fromProcess = true;
      }
      if (selectedProcess.vc !== null) {
        next.vc = selectedProcess.vc;
        fromProcess = true;
      }
      if (selectedProcess.fz !== null) {
        next.fz = selectedProcess.fz;
        fromProcess = true;
      }
    }

    setParams(next);
    setIsFromProcess(fromProcess);
    setUserEdited(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProcess?.id]);

  const setParam = (key: keyof MLCuttingParams, value: number) => {
    setParams((prev) => ({ ...prev, [key]: value }));
    setUserEdited(true);
  };

  const resetToDefaults = () => {
    setParams({ ...ML_CUTTING_DEFAULTS });
    setUserEdited(false);
    setIsFromProcess(false);
  };

  return {
    params,
    setParam,
    resetToDefaults,
    isDirtyFromDefaults: !isDeepEqualToDefaults(params),
    isFromProcess,
    isUserEdited: userEdited,
  };
}
